import { IconDownload, IconFileTypeXls, IconTrash } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import { useState } from 'react';
import { Row, Col } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import {
    NSCard,
    NSButton,
    NSDropzone,
} from 'bricks';
import { ISelectedHTML5File } from 'bricks/types';
import { CONTENT_TYPE, EXCEL_TYPE } from 'bricks/constants';
import PageTitle from 'components/PageTitle/PageTitle';
import useToast from 'hooks/useToast';
import RenderIf from '../../../components/RenderIf/RenderIf';
import { URLS } from '../../../services/urls';
import { useAuthContext } from '../../../contexts/AuthContext';
import useGetBulkUploadTemplate from '../hooks/useGetBulkUploadTemplate';
import useBulkUploadDeals from '../hooks/useBulkUploadDeals';
import './BulkUpload.scss';

const DealsBulkUpload = () => {
    const [pendingFiles, setPendingFiles] = useState<{ id: number, file: ISelectedHTML5File }[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const navigate = useNavigate();
    const { selectedOrganizationId } = useAuthContext();
    const { handleDownload, isDownloading } = useGetBulkUploadTemplate(selectedOrganizationId);
    const { showSuccess } = useToast();
    const { handleBulkUpload, isUploading } = useBulkUploadDeals(selectedOrganizationId, {
        onSuccess: response => {
            if (response.errors.length > 0) {
                const errorList = response.errors.map(e => `• ${e.dealName || `Line ${e.line}`}: ${e.message}`).join('\n');
                setErrorMessage(`Upload failed:\n${errorList}`);
            } else {
                showSuccess('Deals created successfully!');
                setTimeout(() => {
                    navigate(URLS.DEALS.HOME);
                }, 1000);
            }
        },
        onError: error => {
            setErrorMessage(`${error instanceof Error ? error.message : 'Upload failed: Unknown error'}`);
        },
    });

    const handleSubmitUpload = async () => {
        if (pendingFiles.length === 0) return;

        setErrorMessage(null);
        try {
            const selectedFile = pendingFiles[0].file;
            await handleBulkUpload(selectedFile.fileObject);
        } catch (error) {
            setErrorMessage(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    const handleBack = () => {
        navigate(URLS.DEALS.HOME);
    };

    const handleRemoveFile = (id: number) => {
        setErrorMessage(null);
        setPendingFiles(prevFiles => prevFiles.filter(file => file.id !== id));
    };

    const acceptedMimeTypes = [CONTENT_TYPE.CSV, ...Object.values(EXCEL_TYPE)].join(', ');

    return (
        <div className="deals-bulk-upload">
            <PageTitle
                title="Create deal from template"
                breadcrumbBackLinkCallback={() => handleBack()}
                breadcrumbBackLinkText="Deals"
                subtitle="Use our template to quickly upload your existing deals"
            >
                <Row className="d-flex align-items-center mr-0">
                    <NSButton
                        id="finalize-upload-button"
                        text="Finalize"
                        callback={handleSubmitUpload}
                        color="primary"
                        disabled={pendingFiles.length === 0 || isUploading || errorMessage !== null}
                        outline={false}
                        includeTooltip
                    />
                </Row>
            </PageTitle>
            <NSCard>
                <Row className="d-flex align-items-center justify-content-between px-3 py-2">
                    <Col xs="auto">
                        <h4 className="mb-0">1</h4>
                    </Col>

                    <Col className="d-flex flex-column">
                        <h4 className="mb-1 text-white">Download and fill out template</h4>
                        <span className="mb-0 text-muted">Use our provided template to help map your deal information to Northspyre Deal attributes.</span>
                    </Col>

                    <Col xs="auto">
                        <NSButton
                            text="Download Excel template"
                            callback={handleDownload}
                            color="light"
                            icon={IconDownload}
                            outline={false}
                            disabled={isDownloading || isUploading}
                        />
                    </Col>
                </Row>
            </NSCard>
            <NSCard>
                <Row className="d-flex align-items-center justify-content-between px-3 py-2">
                    <Col xs="auto">
                        <h4 className="mb-0">2</h4>
                    </Col>
                    <Col className="d-flex flex-column">
                        <h4 className="mb-1 text-white">Upload completed template</h4>
                        <span className="mb-0 text-muted">Drag-and-drop your completed template below</span>
                    </Col>
                </Row>
                <div className="px-3 pb-3 pl-5">
                    <RenderIf isTrue={pendingFiles.length === 0}>
                        <NSDropzone
                            pendingFiles={pendingFiles}
                            setPendingFiles={files => {
                                setErrorMessage(null);
                                setPendingFiles(files);
                            }}
                            accept={acceptedMimeTypes}
                            dragAndDropText="Drag & drop template file or"
                        />
                    </RenderIf>

                    {errorMessage && (
                        <div className="deals-bulk-upload__error-message mt-2">{errorMessage}</div>
                    )}

                    {pendingFiles.map(file => (
                        <div key={file.id} className="mt-2">
                            <div className="deals-bulk-upload__file-item">
                                <div className="deals-bulk-upload__file-item-name">
                                    <NSIcon icon={IconFileTypeXls} className="text-dark mr-2" />
                                    <span className="text-dark text-truncate">{file.file.name}</span>
                                </div>
                                <NSButton
                                    className="text-dark"
                                    icon={IconTrash}
                                    callback={() => handleRemoveFile(file.id)}
                                    size="sm"
                                    disabled={isUploading}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </NSCard>
        </div>
    );
};

export default DealsBulkUpload;
